// apps/frontend/src/lib/pipeline-preview/transformers/join.ts
import type { PipelineNode, JoinType } from '@/pages/pipelines/types/pipeline';
import type { ExecutionContext, TransformerResult } from '../types';
import { inferSchema } from '../utils/schema';
import { analyzeJoin, formatDiagnosticReport } from '../utils/join-debugger';

/**
 * Apply JOIN node to merge two data streams
 *
 * Note: inputData should be an array of two datasets [leftData, rightData]
 */
export async function joinData(
  node: PipelineNode,
  inputData: unknown[],
  _context: ExecutionContext
): Promise<TransformerResult> {
  const startTime = performance.now();

  try {
    const config = node.data.config as any;
    if (!config?.leftKey || !config?.rightKey) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'Join keys not configured',
      };
    }

    // Validate input data
    if (!Array.isArray(inputData) || inputData.length !== 2) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'JOIN node requires exactly two input datasets',
      };
    }

    const [leftDataRaw, rightDataRaw] = inputData;
    const leftData = Array.isArray(leftDataRaw) ? leftDataRaw : [];
    const rightData = Array.isArray(rightDataRaw) ? rightDataRaw : [];

    const { joinType, leftKey, rightKey } = config;

    // Perform join based on type
    let joinedData: unknown[];
    switch (joinType as JoinType) {
      case 'INNER':
        joinedData = innerJoin(leftData, rightData, leftKey, rightKey);
        break;
      case 'LEFT':
        joinedData = leftJoin(leftData, rightData, leftKey, rightKey);
        break;
      case 'RIGHT':
        joinedData = rightJoin(leftData, rightData, leftKey, rightKey);
        break;
      case 'FULL':
        joinedData = fullJoin(leftData, rightData, leftKey, rightKey);
        break;
      default:
        joinedData = innerJoin(leftData, rightData, leftKey, rightKey);
    }

    // Infer schema from joined data
    const schema = inferSchema(joinedData);

    // Auto-diagnostic: Log warning if JOIN produces few/no records
    if (joinedData.length === 0 || joinedData.length < Math.min(leftData.length, rightData.length) * 0.1) {
      console.warn(`⚠️  JOIN produced only ${joinedData.length} records from ${leftData.length} + ${rightData.length} inputs`);

      const diagnostic = analyzeJoin(leftData, rightData, leftKey, rightKey);
      const report = formatDiagnosticReport(diagnostic);
      console.log(report);

      // Save to window for debugging
      if (import.meta.env.DEV) {
        (window as any).__lastJoinDiag = diagnostic;
        console.log('💾 Diagnostic saved to window.__lastJoinDiag');
      }
    }

    const endTime = performance.now();
    return {
      success: true,
      data: joinedData,
      schema,
      stats: {
        rows: joinedData.length,
        executionTime: Math.round(endTime - startTime),
      },
    };
  } catch (error: any) {
    const endTime = performance.now();
    return {
      success: false,
      data: [],
      schema: { columns: [] },
      stats: { rows: 0, executionTime: Math.round(endTime - startTime) },
      error: error.message || 'Join failed',
    };
  }
}

/**
 * Build a hash map from data array for efficient lookups
 */
function buildHashMap(
  data: unknown[],
  keyField: string
): Map<unknown, unknown[]> {
  const map = new Map<unknown, unknown[]>();

  data.forEach(row => {
    const key = (row as Record<string, unknown>)[keyField];
    // Skip null/undefined keys (SQL semantics: nulls don't match)
    if (key === null || key === undefined) return;

    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(row);
  });

  return map;
}

/**
 * Inner join: Only matching rows
 */
function innerJoin(
  leftData: unknown[],
  rightData: unknown[],
  leftKey: string,
  rightKey: string
): unknown[] {
  const result: unknown[] = [];

  // Build hash map for right data
  const rightMap = buildHashMap(rightData, rightKey);

  // Join left with right
  leftData.forEach(leftRow => {
    const key = (leftRow as Record<string, unknown>)[leftKey];
    // Skip null keys (they won't match anything)
    if (key === null || key === undefined) return;

    const rightRows = rightMap.get(key) || [];

    rightRows.forEach(rightRow => {
      result.push({ ...leftRow as object, ...rightRow as object });
    });
  });

  return result;
}

/**
 * Left join: All left rows + matching right rows (null if no match)
 */
function leftJoin(
  leftData: unknown[],
  rightData: unknown[],
  leftKey: string,
  rightKey: string
): unknown[] {
  const result: unknown[] = [];

  // Build hash map for right data
  const rightMap = buildHashMap(rightData, rightKey);

  // Get all right keys for null filling
  const rightKeys = rightData.length > 0
    ? Object.keys(rightData[0] as Record<string, unknown>)
    : [];

  // Join left with right
  leftData.forEach(leftRow => {
    const key = (leftRow as Record<string, unknown>)[leftKey];

    // Handle null keys: keep the row but don't try to match
    if (key === null || key === undefined) {
      const nullRow = { ...leftRow as object };
      rightKeys.forEach(k => {
        if (!(k in nullRow)) {
          (nullRow as any)[k] = null;
        }
      });
      result.push(nullRow);
      return;
    }

    const rightRows = rightMap.get(key);

    if (rightRows && rightRows.length > 0) {
      rightRows.forEach(rightRow => {
        result.push({ ...leftRow as object, ...rightRow as object });
      });
    } else {
      // No match - add left row with null right columns
      const nullRow = { ...leftRow as object };
      rightKeys.forEach(k => {
        if (!(k in nullRow)) {
          (nullRow as any)[k] = null;
        }
      });
      result.push(nullRow);
    }
  });

  return result;
}

/**
 * Right join: All right rows + matching left rows (null if no match)
 */
function rightJoin(
  leftData: unknown[],
  rightData: unknown[],
  leftKey: string,
  rightKey: string
): unknown[] {
  const result: unknown[] = [];

  // Build hash map for left data
  const leftMap = buildHashMap(leftData, leftKey);

  // Get all left keys for null filling
  const leftKeys = leftData.length > 0
    ? Object.keys(leftData[0] as Record<string, unknown>)
    : [];

  // Join right with left
  rightData.forEach(rightRow => {
    const key = (rightRow as Record<string, unknown>)[rightKey];

    // Handle null keys: keep the row but don't try to match
    if (key === null || key === undefined) {
      const nullRow: any = {};
      leftKeys.forEach(k => {
        nullRow[k] = null;
      });
      result.push({ ...nullRow, ...rightRow as object });
      return;
    }

    const leftRows = leftMap.get(key);

    if (leftRows && leftRows.length > 0) {
      leftRows.forEach(leftRow => {
        result.push({ ...leftRow as object, ...rightRow as object });
      });
    } else {
      // No match - add right row with null left columns
      const nullRow: any = {};
      leftKeys.forEach(k => {
        nullRow[k] = null;
      });
      result.push({ ...nullRow, ...rightRow as object });
    }
  });

  return result;
}

/**
 * Full outer join: All rows from both sides (null for non-matches)
 */
function fullJoin(
  leftData: unknown[],
  rightData: unknown[],
  leftKey: string,
  rightKey: string
): unknown[] {
  const result: unknown[] = [];

  // Build hash map for right data
  const rightMap = buildHashMap(rightData, rightKey);
  const matchedRightKeys = new Set<unknown>();

  // Get all keys for null filling
  const leftKeys = leftData.length > 0
    ? Object.keys(leftData[0] as Record<string, unknown>)
    : [];
  const rightKeys = rightData.length > 0
    ? Object.keys(rightData[0] as Record<string, unknown>)
    : [];

  // Join left with right
  leftData.forEach(leftRow => {
    const key = (leftRow as Record<string, unknown>)[leftKey];

    // Handle null keys: keep the row but don't try to match
    if (key === null || key === undefined) {
      const nullRow = { ...leftRow as object };
      rightKeys.forEach(k => {
        if (!(k in nullRow)) {
          (nullRow as any)[k] = null;
        }
      });
      result.push(nullRow);
      return;
    }

    const rightRows = rightMap.get(key);

    if (rightRows && rightRows.length > 0) {
      rightRows.forEach(rightRow => {
        result.push({ ...leftRow as object, ...rightRow as object });
        matchedRightKeys.add(key);
      });
    } else {
      // No match - add left row with null right columns
      const nullRow = { ...leftRow as object };
      rightKeys.forEach(k => {
        if (!(k in nullRow)) {
          (nullRow as any)[k] = null;
        }
      });
      result.push(nullRow);
    }
  });

  // Add unmatched right rows
  rightData.forEach(rightRow => {
    const key = (rightRow as Record<string, unknown>)[rightKey];
    // Skip null keys (already handled in left pass if left has nulls)
    // and matched keys
    if (key === null || key === undefined || matchedRightKeys.has(key)) {
      return;
    }

    const nullRow: any = {};
    leftKeys.forEach(k => {
      nullRow[k] = null;
    });
    result.push({ ...nullRow, ...rightRow as object });
  });

  // Add unmatched right rows with null keys
  rightData.forEach(rightRow => {
    const key = (rightRow as Record<string, unknown>)[rightKey];
    if (key === null || key === undefined) {
      const nullRow: any = {};
      leftKeys.forEach(k => {
        nullRow[k] = null;
      });
      result.push({ ...nullRow, ...rightRow as object });
    }
  });

  return result;
}
